import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Loader2, Menu, Moon, Sun, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAppContext } from "@/app/providers/AppContext";
import { useTheme } from "@/app/providers/ThemeContext";
import { handleLandingAnchorClick } from "@/landing/landingScroll";
import BrandLogo from "@/shared/components/BrandLogo";

gsap.registerPlugin(ScrollTrigger);

const navLinks = [
  { label: "Tools", href: "#tools" },
  { label: "Pricing", href: "#plans" },
  { label: "FAQ", href: "#faq" },
  { label: "Privacy", href: "/privacy" },
];

const headerStyles = `
  @keyframes nav-pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50%       { opacity: 0.4; transform: scale(0.75); }
  }
  .nav-pulse { animation: nav-pulse 2s infinite; }

  .nav-link {
    position: relative;
    font-family: var(--font-mono);
    font-size: 0.7rem;
    letter-spacing: 0.04em;
    color: rgba(255,255,255,0.52);
    text-decoration: none;
    transition: color 0.2s;
    padding: 4px 0;
  }
  .nav-link::after {
    content: '';
    position: absolute;
    bottom: -2px; left: 0;
    width: 0; height: 1px;
    background: #00B8DB;
    transition: width 0.25s ease;
  }
  .nav-link:hover { color: white; }
  .nav-link:hover::after { width: 100%; }

  .nav-cta-ghost {
    font-family: var(--font-mono);
    font-size: 0.68rem;
    letter-spacing: 0.05em;
    padding: 8px 18px;
    border-radius: 100px;
    border: 1px solid rgba(255,255,255,0.18);
    color: rgba(255,255,255,0.6);
    background: transparent;
    text-decoration: none;
    transition: all 0.2s;
  }
  .nav-cta-ghost:hover {
    border-color: rgba(255,255,255,0.5);
    color: white;
  }
  /* Light theme styles for nav-cta-ghost */
  html:not(.dark) .nav-cta-ghost {
    border-color: rgba(0,0,0,0.2);
    color: rgba(0,0,0,0.7);
  }
  html:not(.dark) .nav-cta-ghost:hover {
    border-color: rgba(0,0,0,0.4);
    color: #111111;
  }
  .nav-cta-solid {
    font-family: var(--font-mono);
    font-size: 0.68rem;
    letter-spacing: 0.05em;
    font-weight: 600;
    padding: 8px 20px;
    border-radius: 100px;
    border: 1px solid var(--lh-cyan, #00B8DB);
    background: var(--lh-cyan, #00B8DB);
    color: #000000;
    text-decoration: none;
    transition: all 0.2s;
    cursor: pointer;
  }
  .nav-cta-solid:hover { background: #19d8ff; border-color: #19d8ff; color: #000000; }

  .header-scrolled {
    background: rgba(13,13,13,0.92) !important;
    backdrop-filter: blur(16px) !important;
    border-bottom-color: rgba(255,255,255,0.07) !important;
  }

  .mobile-menu-toggle { display: inline-flex; }
  .mobile-menu-panel { display: block; }

  @media (min-width: 768px) {
    .mobile-menu-toggle,
    .mobile-menu-panel {
      display: none !important;
    }
  }
`;

export default function Header() {
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  const getHref = (href: string) => {
    if (href.startsWith("#")) {
      return isHomePage ? href : `/${href}`;
    }
    return href;
  };

  const headerRef = useRef(null);
  const logoRef = useRef(null);
  const linksRef = useRef(null);
  const actionsRef = useRef(null);
  const { isAuthenticated, loading } = useAppContext();
  const { theme, setThemeMode } = useTheme();
  const isDark = theme === "dark";
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // ── Entry animation ────────────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline({ delay: 0.1 })
        .fromTo(
          logoRef.current,
          { opacity: 0, x: -16 },
          { opacity: 1, x: 0, duration: 0.5, ease: "power3.out" },
        )
        .fromTo(
          linksRef.current?.querySelectorAll(".nav-link"),
          { opacity: 0, y: -8 },
          {
            opacity: 1,
            y: 0,
            duration: 0.4,
            stagger: 0.06,
            ease: "power2.out",
          },
          "-=0.3",
        )
        .fromTo(
          actionsRef.current?.children,
          { opacity: 0, x: 16 },
          {
            opacity: 1,
            x: 0,
            duration: 0.4,
            stagger: 0.08,
            ease: "power3.out",
          },
          "-=0.35",
        );
    }, headerRef);

    return () => ctx.revert();
  }, []);

  // ── Scroll blur effect ─────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <>
      <style>{headerStyles}</style>
      <header
        ref={headerRef}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          background: scrolled 
            ? (isDark ? "rgba(13,13,13,0.92)" : "rgba(247,247,245,0.92)") 
            : "transparent",
          borderBottom: scrolled 
            ? (isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)") 
            : "1px solid transparent",
          backdropFilter: scrolled ? "blur(16px)" : "none",
          transition:
            "background 0.3s, border-color 0.3s, backdrop-filter 0.3s",
        }}
      >
        <nav
          className="px-4 sm:px-10"
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            height: "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "32px",
          }}
        >
          {/* ── Logo ─────────────────────────────────────────────────── */}
          <a
            ref={logoRef}
            href="/"
            className="rounded-md"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              flexShrink: 0,
              opacity: 0,
              borderRadius: 10,
            }}
          >
            <BrandLogo textColor={isDark ? "white" : "#111111"} />
          </a>

          {/* ── Nav links (desktop) ───────────────────────────────────── */}
          <div
            ref={linksRef}
            className="hidden md:flex items-center gap-7"
            style={{ flex: 1, justifyContent: "center" }}
          >
            {navLinks.map((l) => {
              const href = getHref(l.href);
              return (
                <a
                  key={l.label}
                  href={href}
                  className="nav-link"
                  style={{ color: isDark ? "var(--lh-text-dim)" : "#4b5563" }}
                  onClick={(e) => handleLandingAnchorClick(e, href)}
                  onMouseEnter={e => e.currentTarget.style.color = isDark ? "white" : "#111111"}
                  onMouseLeave={e => e.currentTarget.style.color = isDark ? "var(--lh-text-dim)" : "#4b5563"}
                >
                  {l.label}
                </a>
              );
            })}
          </div>

          {/* ── Actions (desktop) ────────────────────────────────────── */}
          <div
            ref={actionsRef}
            className="hidden md:flex items-center gap-3"
            style={{ flexShrink: 0 }}
          >
            <button
              onClick={() => {
                const newTheme = theme === "dark" ? "light" : "dark";
                setThemeMode(newTheme);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                border: "1px solid rgba(255,255,255,0.1)",
                background: "rgba(255,255,255,0.05)",
                color: isDark ? "#e0e0e0" : "#111110",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                e.currentTarget.style.transform = "translateY(0)";
              }}
              aria-label="Toggle theme"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {loading ? (
              <span
                className="nav-cta-solid"
                aria-label="Initiating session"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 132,
                  gap: 8,
                  cursor: "default",
                }}
              >
                <Loader2 size={14} className="animate-spin" />
                Checking
              </span>
            ) : !isAuthenticated ? (
              <>
                <a href="/login" className="nav-cta-ghost">
                  Log in
                </a>
                <a
                  href={getHref("#plans")}
                  className="nav-cta-solid"
                  onClick={(e) => handleLandingAnchorClick(e, getHref("#plans"))}
                >
                  Get Started →
                </a>
              </>
            ) : (
              <NavLink to="/portal/overview" className="nav-cta-solid">
                Dashboard →
              </NavLink>
            )}
          </div>

          <button
            type="button"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            className="md:hidden mobile-menu-toggle"
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.12)",
              background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
              color: isDark ? "white" : "#111111",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </nav>

        {mobileMenuOpen && (
          <div
            className="md:hidden mobile-menu-panel"
            style={{
              borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
              background: isDark ? "rgba(17,17,17,0.96)" : "rgba(247,247,245,0.96)",
              backdropFilter: "blur(12px)",
            }}
          >
            <div
              style={{
                maxWidth: "1200px",
                margin: "0 auto",
                padding: "12px 16px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                color: isDark ? "white" : "#111111",
              }}
            >
              {navLinks.map((l) => {
                const href = getHref(l.href);
                return (
                  <a
                    key={l.label}
                    href={href}
                    className="nav-link"
                    onClick={(e) => {
                      handleLandingAnchorClick(e, href);
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      display: "block",
                      fontSize: "0.78rem",
                      padding: "8px 0",
                      color: isDark ? "#a0a0a0" : "#6b6b68",
                    }}
                    onMouseEnter={e => e.currentTarget.style.color = isDark ? "white" : "#111111"}
                    onMouseLeave={e => e.currentTarget.style.color = isDark ? "#a0a0a0" : "#6b6b68"}
                  >
                    {l.label}
                  </a>
                );
              })}
              <div className="py-2 flex items-center justify-between border-t border-white/[0.08] mt-2">
                <span className="text-xs font-mono text-gray-500">Theme</span>
                <button
                  onClick={() => {
                    const newTheme = theme === "dark" ? "light" : "dark";
                    setThemeMode(newTheme);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    border: "1px solid rgba(255,255,255,0.1)",
                    background: "rgba(255,255,255,0.05)",
                    color: isDark ? "#e0e0e0" : "#111110",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                  aria-label="Toggle theme"
                >
                  {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                </button>
              </div>
              {loading ? (
                <span
                  className="nav-cta-solid"
                  aria-label="Initiating session"
                  style={{
                    width: "100%",
                    textAlign: "center",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <Loader2 size={14} className="animate-spin" />
                  Checking
                </span>
              ) : !isAuthenticated ? (
                <>
                  <a
                    href="/login"
                    className="nav-cta-ghost"
                    onClick={() => setMobileMenuOpen(false)}
                    style={{ width: "100%", textAlign: "center" }}
                  >
                    Log in
                  </a>
                  <a
                    href={getHref("#plans")}
                    className="nav-cta-solid"
                    onClick={(e) => {
                      handleLandingAnchorClick(e, getHref("#plans"));
                      setMobileMenuOpen(false);
                    }}
                    style={{ width: "100%", textAlign: "center" }}
                  >
                    Get Started →
                  </a>
                </>
              ) : (
                <NavLink
                  to="/portal/overview"
                  className="nav-cta-solid"
                  onClick={() => setMobileMenuOpen(false)}
                  style={{ width: "100%", textAlign: "center" }}
                >
                  Dashboard →
                </NavLink>
              )}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
